import { supabase } from "@/services/supabaseClient";

export const DEFAULT_WORKING_HOURS = {
  monday: { enabled: true, start: "09:00", end: "22:30" },
  tuesday: { enabled: true, start: "09:00", end: "22:30" },
  wednesday: { enabled: true, start: "09:00", end: "22:30" },
  thursday: { enabled: true, start: "09:00", end: "22:30" },
  friday: { enabled: true, start: "09:00", end: "22:30" },
  saturday: { enabled: false, start: "10:00", end: "14:30" },
  sunday: { enabled: false, start: "10:00", end: "14:30" },
};

export const DEFAULT_APPOINTMENT_SETTINGS = {
  slotDuration: "30",
  bufferTime: "0",
  maxAdvanceBooking: "90",
  cancellationPolicy: "24",
  autoConfirm: true,
  allowRescheduling: true,
};

export const DEFAULT_AVAILABILITY_OVERRIDES = [];

const BLOCKING_STATUSES = new Set(["scheduled", "confirmed", "pending", "in_progress"]);
const MINUTES_PER_DAY = 24 * 60;
const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const DAY_NAME_TO_KEY = {
  Sunday: "sunday",
  Monday: "monday",
  Tuesday: "tuesday",
  Wednesday: "wednesday",
  Thursday: "thursday",
  Friday: "friday",
  Saturday: "saturday",
};

const DAY_KEY_TO_NAME = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

const MONDAY_FIRST_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_ALIAS_TO_KEY = {
  sun: "sunday",
  sunday: "sunday",
  mon: "monday",
  monday: "monday",
  tue: "tuesday",
  tues: "tuesday",
  tuesday: "tuesday",
  wed: "wednesday",
  weds: "wednesday",
  wednesday: "wednesday",
  thu: "thursday",
  thur: "thursday",
  thurs: "thursday",
  thursday: "thursday",
  fri: "friday",
  friday: "friday",
  sat: "saturday",
  saturday: "saturday",
};

function parseMinutes(timeHHMM) {
  const match = String(timeHHMM || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function toHHMM(totalMinutes) {
  const normalized = ((Number(totalMinutes || 0) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hh = String(Math.floor(normalized / 60)).padStart(2, "0");
  const mm = String(normalized % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function resolveWorkingDayMinuteWindow(workingDay) {
  const startMinutes = parseMinutes(workingDay?.start);
  const rawEndMinutes = parseMinutes(workingDay?.end);
  if (!workingDay?.enabled || startMinutes == null || rawEndMinutes == null) return null;
  if (rawEndMinutes === startMinutes) return null;

  return {
    startMinutes,
    endMinutes:
      rawEndMinutes < startMinutes
        ? rawEndMinutes + MINUTES_PER_DAY
        : rawEndMinutes,
  };
}

function toLocalBounds(dateISO) {
  const base = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  const end = new Date(base);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

function toDateISO(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysToDateISO(dateISO, amount) {
  const date = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateISO;
  date.setDate(date.getDate() + amount);
  return toDateISO(date);
}

function toLogicalDateTimeISOString(dateISO, minutesFromStartOfDate) {
  const date = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setMinutes(minutesFromStartOfDate);
  return date.toISOString();
}

function getMinutesFromDate(date = new Date()) {
  return date.getHours() * 60 + date.getMinutes();
}

function normalizeWeekdayKey(value) {
  const key = DAY_ALIAS_TO_KEY[String(value || "").trim().toLowerCase()];
  return key || "";
}

function normalizeWeekdayName(value) {
  const key = normalizeWeekdayKey(value);
  return DAY_KEY_TO_NAME[key] || "";
}

function readWorkingHoursDay(workingHours, key) {
  if (!workingHours || typeof workingHours !== "object") return null;

  if (workingHours[key] && typeof workingHours[key] === "object") {
    return workingHours[key];
  }

  const canonicalName = DAY_KEY_TO_NAME[key];
  if (canonicalName && workingHours[canonicalName] && typeof workingHours[canonicalName] === "object") {
    return workingHours[canonicalName];
  }

  const aliasMatch = Object.keys(workingHours).find(
    (candidate) => normalizeWeekdayKey(candidate) === key
  );
  if (!aliasMatch) return null;

  const next = workingHours[aliasMatch];
  return next && typeof next === "object" ? next : null;
}

function normalizeEnabledFlag(value) {
  if (value === true) return true;
  if (value === false) return false;

  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function normalizeDateOnly(dateLike) {
  if (!dateLike) return null;

  if (typeof dateLike === "string") {
    const trimmed = dateLike.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const next = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      if (!Number.isNaN(next.getTime())) return next;
    }
  }

  const parsed = new Date(dateLike);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

function getCalendarDayOrdinal(dateLike) {
  const normalized = normalizeDateOnly(dateLike);
  if (!normalized) return null;
  return Math.floor(
    Date.UTC(
      normalized.getFullYear(),
      normalized.getMonth(),
      normalized.getDate()
    ) / 86400000
  );
}

function diffCalendarDays(fromDateLike, toDateLike) {
  const fromOrdinal = getCalendarDayOrdinal(fromDateLike);
  const toOrdinal = getCalendarDayOrdinal(toDateLike);
  if (fromOrdinal == null || toOrdinal == null) return null;
  return toOrdinal - fromOrdinal;
}

function getWeekStartOrdinal(dateLike) {
  const normalized = normalizeDateOnly(dateLike);
  if (!normalized) return null;
  const weekdayIndex = (normalized.getDay() + 6) % 7;
  const start = new Date(normalized);
  start.setDate(normalized.getDate() - weekdayIndex);
  return getCalendarDayOrdinal(start);
}

function diffCalendarWeeks(fromDateLike, toDateLike) {
  const fromWeekOrdinal = getWeekStartOrdinal(fromDateLike);
  const toWeekOrdinal = getWeekStartOrdinal(toDateLike);
  if (fromWeekOrdinal == null || toWeekOrdinal == null) return null;
  return Math.floor((toWeekOrdinal - fromWeekOrdinal) / 7);
}

function normalizeScheduleStartDate(value, fallbackDateLike = new Date()) {
  const normalized = normalizeDateOnly(value) || normalizeDateOnly(fallbackDateLike);
  return normalized ? toDateISO(normalized) : toDateISO(new Date());
}

function normalizeOverrideKind(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "available") return "available";
  if (normalized === "break") return "break";
  return "unavailable";
}

function compareDateISO(a, b) {
  return String(a || "").localeCompare(String(b || ""));
}

function normalizeTimeValue(value, fallback = "09:00") {
  const parsed = parseMinutes(value);
  return parsed == null ? fallback : toHHMM(parsed);
}

export function normalizeAvailabilityOverrides(overrides = []) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((entry, index) => {
      const startDateRaw =
        entry?.startDate || entry?.start_date || entry?.date || entry?.fromDate;
      const endDateRaw =
        entry?.endDate || entry?.end_date || entry?.date || entry?.toDate || startDateRaw;
      const startDate = normalizeDateOnly(startDateRaw);
      const endDate = normalizeDateOnly(endDateRaw);
      if (!startDate || !endDate) return null;

      const orderedStart = startDate <= endDate ? startDate : endDate;
      const orderedEnd = startDate <= endDate ? endDate : startDate;
      const kind = normalizeOverrideKind(entry?.kind || entry?.type);
      const startDateISO = toDateISO(orderedStart);
      const endDateISO = toDateISO(orderedEnd);

      const keepsTimeRange = kind === "available" || kind === "break";

      return {
        id:
          String(entry?.id || "").trim() ||
          `override-${startDateISO}-${endDateISO}-${kind}-${index + 1}`,
        kind,
        label: String(entry?.label || entry?.reason || "").trim(),
        startDate: startDateISO,
        endDate: endDateISO,
        startTime:
          keepsTimeRange
            ? normalizeTimeValue(
                entry?.startTime || entry?.openTime,
                kind === "break" ? "12:00" : "09:00"
              )
            : null,
        endTime:
          keepsTimeRange
            ? normalizeTimeValue(
                entry?.endTime || entry?.closeTime,
                kind === "break" ? "13:00" : "17:00"
              )
            : null,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const startDiff = compareDateISO(left?.startDate, right?.startDate);
      if (startDiff !== 0) return startDiff;

      const endDiff = compareDateISO(left?.endDate, right?.endDate);
      if (endDiff !== 0) return endDiff;

      if (left?.kind === right?.kind) {
        return String(left?.startTime || "").localeCompare(String(right?.startTime || ""));
      }
      return left?.kind === "unavailable" ? -1 : 1;
    });
}

export function normalizeWorkingHours(workingHours) {
  return WEEKDAY_KEYS.reduce((acc, key) => {
    const fallback = DEFAULT_WORKING_HOURS[key] || {
      enabled: false,
      start: "09:00",
      end: "17:00",
    };
    const next = readWorkingHoursDay(workingHours, key) || {};
    acc[key] = {
      enabled: normalizeEnabledFlag(next?.enabled),
      start: String(next?.start || fallback.start || "09:00").trim() || "09:00",
      end: String(next?.end || fallback.end || "17:00").trim() || "17:00",
    };
    return acc;
  }, {});
}

function hasEnabledWorkingHours(workingHours) {
  return WEEKDAY_KEYS.some((key) => normalizeEnabledFlag(readWorkingHoursDay(workingHours, key)?.enabled));
}

function hasUsableSchedule(schedule = {}) {
  const frequencyType = String(schedule?.frequencyType || "").trim().toLowerCase();
  if (frequencyType === "daily") return true;

  return (
    Array.isArray(schedule?.weeklyDays) &&
    schedule.weeklyDays.some((day) => Boolean(normalizeWeekdayKey(day)))
  );
}

function normalizeSchedule(
  schedule = {},
  workingHours = DEFAULT_WORKING_HOURS,
  fallbackStartDateLike = new Date()
) {
  const normalizedWorkingHours = normalizeWorkingHours(workingHours);
  const derived = deriveScheduleFromWorkingHours(normalizedWorkingHours);
  const rawWeeklyDays = Array.isArray(schedule?.weeklyDays) ? schedule.weeklyDays : [];
  const normalizedWeeklyDays = rawWeeklyDays
    .map((day) => normalizeWeekdayName(day))
    .filter(Boolean);

  return {
    ...derived,
    frequencyType:
      String(schedule?.frequencyType || "").trim() === "daily" ? "daily" : "weekly",
    everyCount: Math.max(1, Number(schedule?.everyCount || derived.everyCount || 1) || 1),
    weeklyDays:
      normalizedWeeklyDays.length > 0
        ? normalizedWeeklyDays
        : derived.weeklyDays,
    openTime: String(schedule?.openTime || derived.openTime || "09:00").trim() || "09:00",
    closeTime: String(schedule?.closeTime || derived.closeTime || "17:00").trim() || "17:00",
    open24: schedule?.open24 === true || derived.open24 === true,
    startDate: normalizeScheduleStartDate(
      schedule?.startDate || schedule?.start_date || schedule?.anchorDate,
      fallbackStartDateLike
    ),
  };
}

function normalizeConfig(row) {
  const rawSchedule =
    row?.availability?.schedule && typeof row.availability.schedule === "object"
      ? row.availability.schedule
      : {};
  const rawWorkingHours =
    row?.availability?.workingHours && typeof row.availability.workingHours === "object"
      ? row.availability.workingHours
      : null;
  const overrides = normalizeAvailabilityOverrides(row?.availability?.overrides);

  const workingHours = hasEnabledWorkingHours(rawWorkingHours)
    ? normalizeWorkingHours(rawWorkingHours)
    : hasUsableSchedule(rawSchedule)
      ? buildWorkingHoursFromSchedule(rawSchedule)
      : normalizeWorkingHours(rawWorkingHours);

  return {
    workingHours,
    appointmentSettings: {
      ...DEFAULT_APPOINTMENT_SETTINGS,
      ...(row?.appointment_settings || {}),
    },
    consultationFees:
      row?.consultation_fees && typeof row.consultation_fees === "object"
        ? row.consultation_fees
        : {},
    schedule: normalizeSchedule(rawSchedule, workingHours, row?.updated_at || new Date()),
    overrides,
  };
}

export function buildWorkingHoursFromSchedule({
  frequencyType = "daily",
  weeklyDays = [],
  openTime = "09:00",
  closeTime = "17:00",
  open24 = false,
} = {}) {
  const normalizedFrequency = String(frequencyType || "daily").toLowerCase().trim();
  const selectedDays = new Set(
    (Array.isArray(weeklyDays) ? weeklyDays : [])
      .map((day) => normalizeWeekdayKey(day))
      .filter(Boolean)
  );

  const start = open24 ? "00:00" : String(openTime || "09:00").trim() || "09:00";
  const end = open24 ? "23:59" : String(closeTime || "17:00").trim() || "17:00";
  const enableAllDays = normalizedFrequency === "daily";

  return WEEKDAY_KEYS.reduce((acc, key) => {
    const fallback = DEFAULT_WORKING_HOURS[key] || {
      enabled: false,
      start,
      end,
    };

    acc[key] = {
      ...fallback,
      enabled: enableAllDays ? true : selectedDays.has(key),
      start,
      end,
    };
    return acc;
  }, {});
}

export function deriveScheduleFromWorkingHours(workingHours = DEFAULT_WORKING_HOURS) {
  const normalizedWorkingHours = normalizeWorkingHours(workingHours);
  const enabledKeys = MONDAY_FIRST_KEYS.filter(
    (key) => normalizedWorkingHours?.[key]?.enabled
  );
  const firstEnabled = enabledKeys[0] ? normalizedWorkingHours[enabledKeys[0]] : null;
  const weeklyDays = enabledKeys.map((key) => DAY_KEY_TO_NAME[key]);
  const open24 =
    enabledKeys.length > 0 &&
    enabledKeys.every(
      (key) =>
        normalizedWorkingHours?.[key]?.start === "00:00" &&
        normalizedWorkingHours?.[key]?.end === "23:59"
    );

  return {
    frequencyType: enabledKeys.length === MONDAY_FIRST_KEYS.length ? "daily" : "weekly",
    everyCount: 1,
    weeklyDays,
    openTime: firstEnabled?.start || "09:00",
    closeTime: firstEnabled?.end || "17:00",
    open24,
  };
}

function getWeekdayKey(dateISO) {
  const date = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
}

function slotOverlaps(startMinutes, endMinutes, busyStart, busyEnd) {
  return startMinutes < busyEnd && endMinutes > busyStart;
}

function getReferenceDate(dateLike = new Date()) {
  return normalizeDateOnly(dateLike) || normalizeDateOnly(new Date()) || new Date();
}

function normalizeWeeklyDayKeys(schedule = {}, workingHours = DEFAULT_WORKING_HOURS) {
  const fromSchedule = new Set(
    (Array.isArray(schedule?.weeklyDays) ? schedule.weeklyDays : [])
      .map((day) => normalizeWeekdayKey(day))
      .filter(Boolean)
  );

  if (fromSchedule.size > 0) return fromSchedule;

  return new Set(
    WEEKDAY_KEYS.filter((key) => workingHours?.[key]?.enabled)
  );
}

function doesOverrideMatchDate(override, dateISO) {
  if (!override?.startDate || !override?.endDate || !dateISO) return false;
  return (
    compareDateISO(override.startDate, dateISO) <= 0 &&
    compareDateISO(override.endDate, dateISO) >= 0
  );
}

function getOverrideSpanDays(override) {
  return diffCalendarDays(override?.startDate, override?.endDate);
}

function getMatchingAvailabilityOverride(config, dateISO) {
  const overrides = normalizeAvailabilityOverrides(config?.overrides);
  const matches = overrides.filter(
    (override) =>
      override?.kind !== "break" && doesOverrideMatchDate(override, dateISO)
  );
  if (matches.length === 0) return null;

  const blocked = matches.find((override) => override.kind === "unavailable");
  if (blocked) return blocked;

  return matches
    .filter((override) => override.kind === "available")
    .sort((left, right) => {
      const spanDiff = (getOverrideSpanDays(left) || 0) - (getOverrideSpanDays(right) || 0);
      if (spanDiff !== 0) return spanDiff;
      return compareDateISO(left?.startDate, right?.startDate);
    })[0] || null;
}

function getBreakOverridesForDate(config, dateISO) {
  return normalizeAvailabilityOverrides(config?.overrides)
    .filter((override) => override?.kind === "break" && doesOverrideMatchDate(override, dateISO))
    .filter((override) => parseMinutes(override?.endTime) > parseMinutes(override?.startTime));
}

export function getEffectiveWorkingDayForDate(config, dateISO) {
  const weekdayKey = getWeekdayKey(dateISO);
  const baseWorkingDay = config?.workingHours?.[weekdayKey] || {
    enabled: false,
    start: "09:00",
    end: "17:00",
  };
  const override = getMatchingAvailabilityOverride(config, dateISO);
  const breaks = getBreakOverridesForDate(config, dateISO);

  if (override?.kind === "unavailable") {
    return {
      ...baseWorkingDay,
      enabled: false,
      override,
      breaks: [],
    };
  }

  if (override?.kind === "available") {
    return {
      enabled: true,
      start: normalizeTimeValue(override?.startTime, baseWorkingDay?.start || "09:00"),
      end: normalizeTimeValue(override?.endTime, baseWorkingDay?.end || "17:00"),
      override,
      breaks,
    };
  }

  return {
    ...baseWorkingDay,
    override: null,
    breaks,
  };
}

function isDateEnabledBySchedule(config, dateLike) {
  const date = normalizeDateOnly(dateLike);
  if (!date) return false;

  const dateISO = toDateISO(date);
  const matchedOverride = getMatchingAvailabilityOverride(config, dateISO);
  if (matchedOverride?.kind === "unavailable") return false;
  if (matchedOverride?.kind === "available") return true;

  const weekdayKey = getWeekdayKey(dateISO);
  const workingHours = config?.workingHours || DEFAULT_WORKING_HOURS;
  if (!workingHours?.[weekdayKey]?.enabled) return false;

  const schedule = config?.schedule || {};
  const normalizedFrequency = String(schedule?.frequencyType || "").trim().toLowerCase();
  const everyCount = Math.max(1, Number(schedule?.everyCount || 1) || 1);
  const startDateISO = normalizeScheduleStartDate(schedule?.startDate, new Date());

  if (normalizedFrequency === "daily") {
    const dayOffset = diffCalendarDays(startDateISO, dateISO);
    return dayOffset != null && dayOffset >= 0 && dayOffset % everyCount === 0;
  }

  const selectedWeekdays = normalizeWeeklyDayKeys(schedule, workingHours);
  if (!selectedWeekdays.has(weekdayKey)) return false;

  const weekOffset = diffCalendarWeeks(startDateISO, dateISO);
  return weekOffset != null && weekOffset >= 0 && weekOffset % everyCount === 0;
}

export function summarizeAvailabilityConfig(config, referenceDateLike = new Date()) {
  const referenceDate = getReferenceDate(referenceDateLike);

  for (let offset = 0; offset < 7; offset += 1) {
    const day = new Date(referenceDate);
    day.setDate(referenceDate.getDate() + offset);
    if (!isDateEnabledBySchedule(config, day)) continue;
    if (offset === 0) return "Available Today";
    if (offset === 1) return "Available Tomorrow";
    return "Available This Week";
  }

  return "Unavailable";
}

export async function getDoctorAvailabilityConfig(doctorId) {
  if (!doctorId) {
    return normalizeConfig(null);
  }

  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("availability,appointment_settings,updated_at")
    .eq("user_id", doctorId)
    .maybeSingle();

  if (error) throw error;
  return normalizeConfig(data || null);
}

export async function loadDoctorAvailabilityProfile(userId) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) {
    return normalizeConfig(null);
  }

  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("availability,appointment_settings,consultation_fees,updated_at")
    .eq("user_id", normalizedUserId)
    .maybeSingle();

  if (error) throw error;
  return normalizeConfig(data || null);
}

async function doctorAvailabilityProfileExists(userId) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return false;

  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("user_id")
    .eq("user_id", normalizedUserId)
    .maybeSingle();

  if (error) throw error;
  return Boolean(data?.user_id);
}

export async function saveDoctorAvailabilityProfile(
  userId,
  {
    workingHours,
    appointmentSettings,
    consultationFees,
    schedule,
    overrides,
  } = {}
) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) throw new Error("Doctor user id is required.");

  const current = await loadDoctorAvailabilityProfile(normalizedUserId);
  const resolvedWorkingHours = workingHours
    ? normalizeWorkingHours(workingHours)
    : schedule
      ? buildWorkingHoursFromSchedule(schedule)
      : current.workingHours;
  const derivedScheduleFromWorkingHours = deriveScheduleFromWorkingHours(
    resolvedWorkingHours
  );
  const resolvedScheduleInput = workingHours
    ? {
        ...current.schedule,
        ...(schedule || {}),
        frequencyType: derivedScheduleFromWorkingHours.frequencyType,
        weeklyDays: derivedScheduleFromWorkingHours.weeklyDays,
        openTime: derivedScheduleFromWorkingHours.openTime,
        closeTime: derivedScheduleFromWorkingHours.closeTime,
        open24: derivedScheduleFromWorkingHours.open24,
      }
    : schedule || current.schedule;
  const resolvedSchedule = normalizeSchedule(
    resolvedScheduleInput,
    resolvedWorkingHours,
    schedule?.startDate || current.schedule?.startDate || new Date()
  );

  const payload = {
    user_id: normalizedUserId,
    availability: {
      workingHours: resolvedWorkingHours,
      schedule: resolvedSchedule,
      overrides: normalizeAvailabilityOverrides(overrides || current.overrides),
    },
    appointment_settings: appointmentSettings
      ? {
          ...DEFAULT_APPOINTMENT_SETTINGS,
          ...current.appointmentSettings,
          ...appointmentSettings,
        }
      : current.appointmentSettings,
    consultation_fees:
      consultationFees && typeof consultationFees === "object"
        ? {
            ...current.consultationFees,
            ...consultationFees,
          }
        : current.consultationFees,
    updated_at: new Date().toISOString(),
  };

  const profileExists = await doctorAvailabilityProfileExists(normalizedUserId);

  const { error } = profileExists
    ? await supabase
        .from("doctor_profiles")
        .update({
          availability: payload.availability,
          appointment_settings: payload.appointment_settings,
          consultation_fees: payload.consultation_fees,
          updated_at: payload.updated_at,
        })
        .eq("user_id", normalizedUserId)
    : await supabase
        .from("doctor_profiles")
        .insert(payload);

  if (error) throw error;

  return {
    workingHours: resolvedWorkingHours,
    appointmentSettings: payload.appointment_settings,
    consultationFees: payload.consultation_fees,
    schedule: resolvedSchedule,
    overrides: payload.availability.overrides,
  };
}

export function listBookableDatesFromConfig(
  config,
  { referenceDateLike = new Date(), maxResults = null } = {}
) {
  const referenceDate = getReferenceDate(referenceDateLike);
  const appointmentSettings = {
    ...DEFAULT_APPOINTMENT_SETTINGS,
    ...(config?.appointmentSettings || {}),
  };
  const maxAdvanceBooking = Math.max(
    1,
    Number(appointmentSettings?.maxAdvanceBooking || 30) || 30
  );
  const resolvedMaxResults =
    Number.isFinite(Number(maxResults)) && Number(maxResults) > 0
      ? Math.max(1, Math.floor(Number(maxResults)))
      : Number.POSITIVE_INFINITY;
  const maxDate = new Date(referenceDate);
  maxDate.setDate(referenceDate.getDate() + maxAdvanceBooking);
  const dates = [];

  for (let offset = 0; offset <= maxAdvanceBooking; offset += 1) {
    const day = new Date(referenceDate);
    day.setDate(referenceDate.getDate() + offset);
    if (!isDateEnabledBySchedule(config, day)) continue;
    dates.push(toDateISO(day));
    if (dates.length >= resolvedMaxResults) break;
  }

  return {
    dates,
    maxDateISO: toDateISO(maxDate),
    appointmentSettings,
  };
}

export async function getDoctorBookableDates({
  doctorId,
  referenceDateISO = null,
  maxResults = null,
} = {}) {
  const config = await getDoctorAvailabilityConfig(doctorId);
  return listBookableDatesFromConfig(config, {
    referenceDateLike: referenceDateISO || new Date(),
    maxResults,
  });
}

export async function loadDoctorAvailabilityConfigs(doctorIds = []) {
  const uniqueIds = Array.from(
    new Set((doctorIds || []).map((value) => String(value || "").trim()).filter(Boolean))
  );

  if (uniqueIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("user_id,availability,appointment_settings,updated_at")
    .in("user_id", uniqueIds);

  if (error) throw error;

  const map = new Map();
  (data || []).forEach((row) => {
    map.set(String(row?.user_id || "").trim(), normalizeConfig(row));
  });
  return map;
}

function buildBreakBlockingRanges(workingDay) {
  return (Array.isArray(workingDay?.breaks) ? workingDay.breaks : [])
    .map((entry) => {
      const start = parseMinutes(entry?.startTime);
      const end = parseMinutes(entry?.endTime);
      if (start == null || end == null || end <= start) return null;
      return { start, end, type: "break" };
    })
    .filter(Boolean);
}

export function generateDoctorAvailabilitySlots({
  workingDay,
  dateISO,
  slotDuration = 30,
  bookingDuration = 30,
  bufferTime = 0,
  busyRanges = [],
  now = new Date(),
} = {}) {
  const minuteWindow = resolveWorkingDayMinuteWindow(workingDay);
  if (!minuteWindow) {
    return [];
  }
  const { startMinutes, endMinutes } = minuteWindow;

  const safeSlotDuration = Math.max(5, Number(slotDuration || 30) || 30);
  const safeBookingDuration = Math.max(5, Number(bookingDuration || safeSlotDuration) || safeSlotDuration);
  const safeBufferTime = Math.max(0, Number(bufferTime || 0) || 0);
  const nowDateISO = toDateISO(now);
  const nowMinutes = getMinutesFromDate(now);
  const blockingRanges = [
    ...(Array.isArray(busyRanges) ? busyRanges : []),
    ...buildBreakBlockingRanges(workingDay),
  ];

  const slots = [];
  for (
    let cursor = startMinutes;
    cursor + safeBookingDuration <= endMinutes;
    cursor += safeSlotDuration + safeBufferTime
  ) {
    const slotEnd = cursor + safeBookingDuration;
    const slotDayOffset = Math.floor(cursor / MINUTES_PER_DAY);
    const slotDateISO = addDaysToDateISO(dateISO, slotDayOffset);
    const slotMinuteOfDay = ((cursor % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const isPast =
      slotDateISO < nowDateISO ||
      (slotDateISO === nowDateISO && slotMinuteOfDay <= nowMinutes);
    const isBlocked = blockingRanges.some((range) =>
      slotOverlaps(cursor, slotEnd, range.start, range.end)
    );
    const available = !isPast && !isBlocked;
    slots.push({
      time: toHHMM(cursor),
      available,
      hidden: isPast || isBlocked,
      status: isPast ? "past" : isBlocked ? "blocked" : "available",
    });
  }

  return slots;
}

export async function getDoctorBookingAvailability({
  doctorId,
  dateISO,
  requestedDuration = null,
  excludeAppointmentId = "",
}) {
  if (!doctorId || !dateISO) {
    return {
      slots: [],
      workingDay: null,
      maxDateISO: null,
      appointmentSettings: DEFAULT_APPOINTMENT_SETTINGS,
    };
  }

  const config = await getDoctorAvailabilityConfig(doctorId);
  const workingDay = getEffectiveWorkingDayForDate(config, dateISO);
  const slotDuration = Math.max(5, Number(config.appointmentSettings?.slotDuration || 30) || 30);
  const bookingDuration = Math.max(5, Number(requestedDuration || slotDuration) || slotDuration);
  const bufferTime = Math.max(0, Number(config.appointmentSettings?.bufferTime || 0) || 0);
  const maxAdvanceBooking = Math.max(1, Number(config.appointmentSettings?.maxAdvanceBooking || 30) || 30);
  const now = new Date();
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + maxAdvanceBooking);
  const maxDateISO = toDateISO(maxDate);
  const bookingWindowDayOffset = diffCalendarDays(now, dateISO);

  if (
    !workingDay?.enabled ||
    bookingWindowDayOffset == null ||
    bookingWindowDayOffset < 0 ||
    bookingWindowDayOffset > maxAdvanceBooking ||
    !isDateEnabledBySchedule(config, dateISO)
  ) {
    return {
      slots: [],
      workingDay,
      maxDateISO,
      appointmentSettings: config.appointmentSettings,
    };
  }

  const minuteWindow = resolveWorkingDayMinuteWindow(workingDay);
  if (!minuteWindow) {
    return {
      slots: [],
      workingDay,
      maxDateISO,
      appointmentSettings: config.appointmentSettings,
    };
  }
  const { endMinutes } = minuteWindow;

  const bounds = toLocalBounds(dateISO);
  const bookingRangeEnd =
    endMinutes > MINUTES_PER_DAY
      ? toLogicalDateTimeISOString(dateISO, endMinutes)
      : bounds?.end;
  const { data, error } = await supabase
    .from("appointments")
    .select("id,appointment_date,duration_minutes,status")
    .eq("doctor_id", doctorId)
    .gte("appointment_date", bounds?.start || `${dateISO}T00:00:00.000Z`)
    .lte("appointment_date", bookingRangeEnd || bounds?.end || `${dateISO}T23:59:59.999Z`);

  if (error) throw error;

  const busyRanges = (Array.isArray(data) ? data : [])
    .filter((row) => String(row?.id || "").trim() !== String(excludeAppointmentId || "").trim())
    .filter((row) => BLOCKING_STATUSES.has(String(row?.status || "").toLowerCase()))
    .map((row) => {
      const start = new Date(row?.appointment_date);
      const appointmentDateISO = toDateISO(start);
      const dayOffset =
        appointmentDateISO > dateISO
          ? Math.max(0, diffCalendarDays(dateISO, appointmentDateISO) || 0)
          : 0;
      const startMins = dayOffset * MINUTES_PER_DAY + start.getHours() * 60 + start.getMinutes();
      const duration = Math.max(5, Number(row?.duration_minutes || slotDuration) || slotDuration);
      return {
        start: startMins,
        end: startMins + duration + bufferTime,
      };
    });

  const slots = generateDoctorAvailabilitySlots({
    workingDay,
    dateISO,
    slotDuration,
    bookingDuration,
    bufferTime,
    busyRanges,
    now,
  });

  return {
    slots,
    workingDay,
    maxDateISO,
    appointmentSettings: config.appointmentSettings,
  };
}
